import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { useAnalytics } from 'src/components/settings/analytics';
import { useConfigString } from 'src/contexts/statsig-config-provider';
import { useLocation } from 'src/router/hooks';

import {
  identifyCodeReviewerComment,
  DEFAULT_CODE_REVIEWER_COMMENT_UUIDS,
} from '../conversation/src/utils/code-reviewer-comment-identifiers';
import {
  ApiCommentOrPlaceholder,
  isApiComment,
} from '../conversation-provider/types';

// these attributes will be included on all events from this hook
type SuggestionEvtAttributes = {
  isCodeReviewer: boolean;
  selectedTab: 'overview' | 'diff';
  commentId: number | null;
  pullRequestId: number | null;
};

type CodeSuggestionAnalyticsEventHandlers = {
  publishApplyBtnClickEvt: () => void;
  publishCancelBtnClickEvt: () => void;
  publishCommitBtnClickEvt: () => void;
  publishSuggestionCommittedEvt: () => void;
};

export function useCodeSuggestionsAnalytics(
  apiComment: ApiCommentOrPlaceholder | undefined
): CodeSuggestionAnalyticsEventHandlers {
  const commentUUIDs = useConfigString(
    StatsigFeatureKeys.codeReviewerCommentUuidConfig,
    DEFAULT_CODE_REVIEWER_COMMENT_UUIDS
  );

  const isCodeReviewer: boolean = apiComment?.user
    ? identifyCodeReviewerComment(
        {
          id: apiComment?.user.uuid || '',
          name: apiComment?.user.display_name || '',
        },
        commentUUIDs
      )
    : false;

  const location = useLocation();

  const segments = location.pathname.split('/');
  // Second pop is needed in case there is a trailing slash
  const currentPath = segments.pop() || segments.pop();

  const attributes: SuggestionEvtAttributes = {
    // code suggestions is only available on the diff and overview tab
    selectedTab: currentPath === 'diff' ? 'diff' : 'overview',
    isCodeReviewer,
    commentId: apiComment && isApiComment(apiComment) ? apiComment.id : null,
    pullRequestId:
      apiComment && isApiComment(apiComment) ? apiComment.pullrequest.id : null,
  };

  const { publishButtonClickEvent, publishTrackEvent } = useAnalytics({
    source: 'pullRequestScreen',
    attributes,
  });

  // UI event: User clicks "Apply suggestion" button on a Suggestion Card
  const publishApplyBtnClickEvt = () =>
    publishButtonClickEvent('applyCodeSuggestion');

  // UI event: User clicks "Cancel" button from the commit suggestion modal
  const publishCancelBtnClickEvt = () =>
    publishButtonClickEvent('cancelCommitSuggestion');

  // UI event: User clicks "Commit changes" button from the commit suggestion modal
  const publishCommitBtnClickEvt = () =>
    publishButtonClickEvent('confirmCommitSuggestion');

  // Track event: Code suggestion has successfully been committed to source branch
  const publishSuggestionCommittedEvt = () =>
    publishTrackEvent({
      action: 'committed',
      actionSubject: 'codeSuggestion',
      source: 'pullRequestScreen',
    });

  return {
    publishApplyBtnClickEvt,
    publishCancelBtnClickEvt,
    publishCommitBtnClickEvt,
    publishSuggestionCommittedEvt,
  };
}
